import { Container, Singleton } from 'typescript-ioc'
import { AppService } from './app'
import { IApiResponse } from '@pushly/aqe/lib/interfaces/index'
import aqe from '@pushly/aqe'
import { axiosFetch, isAxiosCancellation, isAxiosResponseError } from '../config/axios-setup'
import { simpleNotification } from '@pushly/aqe/lib/utils'
import { IApiCallOptions } from '@pushly/aqe/lib/interfaces'
import { Feed, MutableFeed } from '@pushly/models/lib/structs/feeds/feed'
import { FeedConfiguration } from '@pushly/models/lib/structs/feeds/feed-configuration'
import { FeedType } from '@pushly/models/lib/enums/feed-type'
import { FeedNotificationPreview } from '@pushly/models/lib/structs/feeds/feed-notification-preview'

interface IFeedPreviewApiResponse {
    notifications: FeedNotificationPreview[]
    estimated_delivery_times: string[]
}

/**
 * Minimal, typed feed shape for selection UIs (e.g. the journey Feed Notification
 * step editor). Exposes only what a selector needs to display and identify a feed:
 * its `id`, display `name`, and `type` (the feed's `FeedType`).
 */
export interface IFeedSelectOption {
    id: number
    name: string
    type: FeedType
}

/**
 * Pure mapper from a full {@link Feed} to the trimmed {@link IFeedSelectOption}
 * shape used by selection UIs. Kept side-effect free so it can be reused and
 * unit-tested independently of any HTTP call.
 */
export function toFeedSelectOption(feed: Feed): IFeedSelectOption {
    return {
        id: feed.id,
        name: feed.name,
        type: feed.feedType,
    }
}

/**
 * Default query params for the domain feed-options lookup.
 *
 * - `pagination: false` tells the v4 domain-feeds endpoint (`GET /v4/domains/:id/feeds`)
 *   to return the domain's COMPLETE feed set in a single response — it bypasses the
 *   default page size and the endpoint's 250-row max page cap — so large domains never
 *   lose feeds in the journey Feed Notification selector.
 * - `status: 'enabled,disabled'` returns BOTH enabled (scheduled) and disabled
 *   (content-only) feeds. The endpoint defaults to enabled-only when no status is sent,
 *   so we must explicitly request both to surface content-only feeds as journey sources.
 *   Serialized as a comma-joined string to mirror the feeds list view (aqe
 *   `FeedsDataView`, which sends `filters.status.join(',')`); the endpoint accepts either
 *   a comma string or a real array.
 *
 * Callers may override any of these via `opts.query` (e.g. re-enable paging with
 * `{ pagination: true, limit }`, narrow status, or add a `search` filter).
 */
export const FEED_OPTION_QUERY_DEFAULTS = { pagination: false, status: 'enabled,disabled' } as const

@Singleton
export class FeedService {
    private appService: AppService

    public constructor() {
        this.appService = Container.get(AppService)
    }

    public async create(domainId: number, createDto: any, opts: IApiCallOptions = {}): Promise<IApiResponse<Feed>> {
        let ok = false
        let feed: Feed

        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        const serviceURL = `${aqe.defaults.publicApiDomain}/v4/domains/${domainId}/feeds`

        try {
            const req = await axiosFetch(
                'post',
                {
                    url: serviceURL,
                    data: createDto,
                },
                opts.cancellationKey,
            )

            ok = true
            feed = Feed.from(req.data.data)

            simpleNotification('success', `Feed '${feed.name}' has been successfully created.`)
        } catch (error) {
            if (isAxiosResponseError(error)) {
                simpleNotification('error', error.response?.data.message)
            }
        }

        if (opts.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, data: feed! }
    }

    public async patchFeed(
        domainId: number,
        feedId: number,
        updateDto: MutableFeed | { enabled: boolean },
        opts: IApiCallOptions = {},
    ): Promise<IApiResponse<Feed>> {
        let ok = false
        let feed: Feed

        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }
        const serviceURL = `${aqe.defaults.publicApiDomain}/v4/domains/${domainId}/feeds/${feedId}`

        try {
            const req = await axiosFetch(
                'patch',
                {
                    url: serviceURL,
                    data: 'serialize' in updateDto ? updateDto.serialize({ exposeUnsetFields: false }) : updateDto,
                },
                opts.cancellationKey,
            )

            ok = true
            feed = Feed.from(req.data.data)

            simpleNotification('success', `Feed '${feed.name}' has been successfully updated.`)
        } catch (error) {
            if (isAxiosResponseError(error)) {
                simpleNotification('error', error.response?.data.message)
            }
        }

        if (opts.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, data: feed! }
    }

    public async archiveFeed(
        domainId: number,
        feedId: number,
        opts: IApiCallOptions = {},
    ): Promise<IApiResponse<boolean>> {
        let ok = false
        let cancelled = false

        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        const serviceURL = `${aqe.defaults.publicApiDomain}/v4/domains/${domainId}/feeds/${feedId}`

        try {
            await axiosFetch(
                'delete',
                {
                    url: serviceURL,
                },
                opts.cancellationKey,
            )

            ok = true

            simpleNotification('success', 'Feed has been successfully archived.')
        } catch (error) {
            // Mirrors fetchDomainFeedOptions: a cancelled request is an intentional
            // supersede/abort and stays silent. Every other failure MUST surface
            // feedback — a network failure, timeout, or a response carrying no message
            // has no body to read, so fall back to a generic message rather than
            // reporting ok=false with no explanation at all.
            cancelled = isAxiosCancellation(error)

            if (!cancelled) {
                const responseMessage = isAxiosResponseError(error) ? error.response?.data?.message : undefined

                simpleNotification('error', responseMessage ?? 'Feed could not be archived. Please try again.')
            }
        }

        if (opts.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, data: ok, cancelled }
    }

    public async fetchFeedById(
        domainId: number,
        feedId: number,
        opts: IApiCallOptions = {},
    ): Promise<{ data: Feed; meta: any; ok: boolean; cancelled: boolean }> {
        let ok = false
        let feed: Feed = new MutableFeed()
        let meta: any
        let cancelled = false

        const serviceURL = `${aqe.defaults.publicApiDomain}/v4/domains/${domainId}/feeds/${feedId}`

        try {
            const res = await axiosFetch(
                'get',
                {
                    url: serviceURL,
                },
                opts?.cancellationKey,
            )

            feed = res.data.data
            ok = true
            meta = res.data.meta
        } catch (error) {
            // `cancelled` is reported so callers can distinguish an aborted request
            // (a superseded selection) from a genuine load failure, matching the
            // sibling loaders in this file. Cancellations stay silent.
            cancelled = isAxiosCancellation(error)

            if (!cancelled && isAxiosResponseError(error)) {
                simpleNotification('error', error.response?.data.message)
            }
        }
        return { ok, data: feed, meta, cancelled }
    }

    /**
     * Fetches the journeys that reference a feed from the read-only
     * `GET /v4/domains/{domainId}/feeds/{feedId}/journey-associations` endpoint.
     *
     * Returns the RAW envelope payload (`res.data.data`, shaped `{ count, journeys }`)
     * without mapping — the {@link getFeedJourneyReferences} seam owns validation and
     * mapping to `FeedJourneyReferenceEntry`. This method NEVER throws: any failure
     * (reject, timeout, non-2xx) resolves to `{ ok: false, data: undefined, cancelled }`,
     * so the seam can degrade gracefully (Req 9.1, 9.4).
     */
    public async fetchFeedJourneyReferences(
        domainId: number,
        feedId: number,
        opts: IApiCallOptions = {},
    ): Promise<{ ok: boolean; data?: unknown; cancelled: boolean }> {
        const serviceURL = `${aqe.defaults.publicApiDomain}/v4/domains/${domainId}/feeds/${feedId}/journey-associations`

        try {
            const res = await axiosFetch(
                'get',
                {
                    url: serviceURL,
                },
                opts.cancellationKey,
            )

            return { ok: true, data: res.data?.data, cancelled: false }
        } catch (error) {
            return { ok: false, data: undefined, cancelled: isAxiosCancellation(error) }
        }
    }

    /**
     * Lists the feeds for a domain and maps each to the trimmed {@link IFeedSelectOption}
     * shape (`id`, `name`, `type`) used by selection UIs such as the journey Feed
     * Notification step editor.
     *
     * Loading/error handling mirrors the domain-scoped option loaders used by other
     * journey editors (e.g. `DomainIntegrationService.fetchAll`): the promise never
     * rejects — callers branch on `ok` / `cancelled`. The request is sent unpaginated
     * ({@link FEED_OPTION_QUERY_DEFAULTS} → `pagination: false`) so the selector always
     * surfaces the domain's COMPLETE feed set in one call regardless of feed count;
     * callers may override via `opts.query`.
     *
     * Both enabled (scheduled) and disabled (content-only) feeds are returned: the
     * defaults send `status: 'enabled,disabled'` explicitly, since the endpoint would
     * otherwise return enabled feeds only. Content-only feeds are valid journey sources.
     */
    public async fetchDomainFeedOptions(
        domainId: number,
        opts: IApiCallOptions = {},
    ): Promise<IApiResponse<IFeedSelectOption[]>> {
        const res: IApiResponse<IFeedSelectOption[]> = {
            ok: false,
            data: [],
            meta: {},
            error: undefined,
            cancelled: false,
        }

        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        const serviceURL = `${aqe.defaults.publicApiDomain}/v4/domains/${domainId}/feeds`

        try {
            const req = await axiosFetch(
                'get',
                {
                    url: serviceURL,
                    config: {
                        params: {
                            ...FEED_OPTION_QUERY_DEFAULTS,
                            ...opts.query,
                        },
                    },
                },
                opts.cancellationKey,
            )

            const { data: reqData, ...meta } = req.data

            res.data = (reqData ?? []).map((datum: any) => toFeedSelectOption(Feed.from(datum)))
            res.meta = meta
            res.ok = true
        } catch (error) {
            res.cancelled = isAxiosCancellation(error)
            res.error = error as Error

            if (!res.cancelled && isAxiosResponseError(error)) {
                simpleNotification('error', error.response?.data.message)
            }
        }

        if (opts.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return res
    }

    public async fetchFeedPreview(
        domainId: number,
        feedConfig: FeedConfiguration,
        url: string,
        feedType: FeedType,
        numOfArticles: number,
        opts: IApiCallOptions = {},
    ): Promise<IApiResponse<IFeedPreviewApiResponse>> {
        let ok = false
        let data: IFeedPreviewApiResponse = {
            notifications: [],
            estimated_delivery_times: [],
        }
        let meta: any
        let cancelled = false

        const serviceURL = `${aqe.defaults.publicApiDomain}/v4/domains/${domainId}/feeds/preview?numOfArticles=${numOfArticles}`

        try {
            const req = await axiosFetch(
                'post',
                {
                    url: serviceURL,
                    data: {
                        previewConfig: {
                            domain_id: domainId,
                            feed_config: feedConfig.serialize(),
                            url,
                            feed_type: feedType,
                        },
                    },
                },
                opts.cancellationKey,
            )

            ok = true

            const notifData = req.data.data?.notifications.map((n) => FeedNotificationPreview.parseJsonObject(n))

            const estDeliveryTimeData = req.data.data?.estimatedDeliveryTimes
            data = {
                notifications: notifData,
                estimated_delivery_times: estDeliveryTimeData,
            }
            meta = req.data.meta
        } catch (error) {
            if (isAxiosResponseError(error)) {
                simpleNotification('error', error.response?.data.message)
            }

            cancelled = isAxiosCancellation(error)
        }

        return { ok, data, meta, cancelled }
    }
}
